import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import SimplePageWrapper from '../../components/shared/simple-page-wrapper';
import {TimeSheetItem, TimeSheetItemStatus, TimeSheetItemType} from '../../features/schedule/types';
import TimeSheetGrid from './components/timesheet-grid';
import {useAppDispatch, useAppSelector} from '../../hooks/hooks';
import {getAllEmployees} from '../../features/employees/short';
import {getProjects} from '../../features/projects/filter';
import {getAbsencesTypes, getAllProjectTypes, getAllShiftsTypes} from '../../features/types';
import moment from 'moment';
import {getWorkCalendar, getWorkCalendarIdByEmployeeId, getWorkCalendars} from '../../features/settings/workcalendar';
import {useIntl} from 'react-intl';
import {absenceAsTimeSheetItem, isEditAbsence, isEditShift, shiftAsTimeSheetItem} from './functions';
import {store} from '../../store/store';
import {clearSelected, selectIdOnDelete} from '../../features/modals';
import {Shift} from '../../features/shifts/types';
import {Employee} from '../../features/employees/types';
import {getEmployee} from '../../features/employees';
import DeleteShiftModal from '../../components/Schedule/modals/delete-shift-modal/deleteShiftModal';
import {Absence} from '../../features/absences/types';
import DeleteAbsenceModal from '../../components/Schedule/modals/delete-absence-modal';
import {TopBar, TopBarMobileSections} from './components/top-bar/index';
import classNames from 'classnames';
import {useMediaQuery} from 'react-responsive';
import {SVGS} from '../../assets/images';

import useTimeSheetView from './hooks/useTimeSheetView';
import {useTimeSheetGroupBy} from './hooks/useTimeSheetGroupBy';
import {useGridViewHandoff} from '../../hooks/useGridViewHandoff';
import {ExpandedGroupsRef} from '../../components/shared/grid/types';
import TimeSheetSearchProvider, {useTimeSheetSearch} from './contexts/search';
import PageHeader from '../../components/shared/page-header';
import TimeTrackingPageBody from '../../components/shared/time-tracking-page-body';
import {setIndicatorLoading} from '../../features/indicator';
import { Year } from '../../features/settings/workcalendar/types';
import { 
    ItemsBatchApprove, 
    ItemsBatchBill, 
    ItemsBatchDisapprove, 
    ItemsBatchUnbill, 
    UpdateAbsenceFromItem, 
    UpdateShiftFromItem, 
    ItemApprove, 
    ItemDisapprove,
    ItemBill,
    ItemUnbill
} from '../../features/schedule/service';
import {showSnack} from '../../features/snacks/snackService';
import styles from './schedule-page.module.scss';
import {NAVIGATION_ROUTES} from '../../routes/NavigationRoute';
import {useLocation, useNavigate} from 'react-router-dom';
import { useBreadcrumbOverride } from '../../components/breadcrumbs/override-context';
import { filterTimesheetItems } from './helpers/filter-timesheet-items';
import usePersistedSummary from '../../hooks/usePersistedSummary';
import { EMPTY_SUMMARY_HOURS } from '../../helpers/empty-summary-hours';
import { getTimesheetSummaryHours } from '../../helpers/timesheet-summary-hours';
import { TIMESHEET_SUMMARY_STORAGE_KEYS } from '../../helpers/summary-storage';
import {
    canApproveTimesheetItems,
    canBillTimesheetItems,
    canDisapproveTimesheetItems,
    canImportTimeTracking,
    canManageTimesheetInternalNote,
    canManageOldTimesheetItems,
    canManageOwnTimesheetItems,
    canManageTimesheetItems,
    canSeeOtherTimesheetItems,
    canSeeAbsenceTypesSection,
    canSeeShiftTypesSection,
    canSeeTimesheetPage,
    canSeeWorkCalendars,
    canUnbillTimesheetItems,
    canViewTimesheetInternalNote,
} from '../../features/profile/entityPermissions';
import { canSeeEmployeesSection, canSeeProjectsSection } from '../../features/profile/navigationVisibility';
import { REmployees } from '../../features/roles/types';
import { PIdNameModel } from '../../features/projects/types';
import { updateTimeTrackingPropertyFilter } from '../../features/time-tracking-filters';

type CopyItemPrefill = {
    kind: 'shift' | 'absence';
    typeId: number;
    hours: number;
    note?: string;
    internalNote?: string;
    isOvertime?: boolean;
    tags?: PIdNameModel[];
};

type CreateShiftPayload = {
    employeeId?: number;
    date?: string;
    lockEmployee?: boolean;
    projectId?: number;
    projectName?: string;
    projects?: PIdNameModel[];
    copy?: CopyItemPrefill;
};

export default function SchedulePage() {
    return (
        <TimeSheetSearchProvider>
            <SchedulePageContent />
        </TimeSheetSearchProvider>
    );
}

function SchedulePageContent() {
    const today = useMemo(() => moment(), []);
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const profile = useAppSelector((selector) => selector.profile);
    const [years, setYears] = useState<Array<Year>>([]);
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
    const [subheaderPortalContainer, setSubheaderPortalContainer] = useState<HTMLDivElement | null>(null);
    const subheaderPortalRef = useRef<HTMLDivElement | null>(null);
    const [isDateSelectorOpen, setIsDateSelectorOpen] = useState(false);
    const [ownEmployeeDetails, setOwnEmployeeDetails] = useState<Employee | null>(null);
    const isMobile = useMediaQuery({ maxWidth: 1000 });

    // selector
    const employees = useAppSelector((selector) => selector.rEmployees);
    const projects = useAppSelector((selector) => selector.eProjects);
    const shiftType = useAppSelector((selector) => selector.types.shifts);
    const absenceType = useAppSelector((selector) => selector.types.absences);
    const projectType = useAppSelector((selector) => selector.types.projects);
    const shifts = useAppSelector((selector) => selector.shifts);
    const absences = useAppSelector((selector) => selector.absences);
    const propertyFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter.propertyFilter);
    const { workCalendars, employeeWorkCalendarId } = useAppSelector((selector) => selector.workCalendar);
    const { data, loading: dataLoading, removeItem, updateItems, updateItem } = useTimeSheetView();
    const { value: search } = useTimeSheetSearch();
    const { groupBy } = useTimeSheetGroupBy();
    const timesheetGridRef = useRef<ExpandedGroupsRef>(null);
    const { restoredGroupKeys, armSnapshot } = useGridViewHandoff('timesheet', String(groupBy), timesheetGridRef);
    const selectDelete = useAppSelector((store) => store.modals.selectedDelete);
    const { setOverride } = useBreadcrumbOverride();
    const canViewSchedule = canSeeTimesheetPage(profile);
    const canEditOwnTimesheetItems = canManageOwnTimesheetItems(profile);
    const canEditTimesheetItems = canManageTimesheetItems(profile);
    const canEditTimesheetInternalNote = canManageTimesheetInternalNote(profile);
    const canViewInternalNote = canViewTimesheetInternalNote(profile);
    const canViewOtherTimesheetItems = canSeeOtherTimesheetItems(profile);
    const canImportTracking = canImportTimeTracking(profile);
    const canEditOldTimesheetItems = canManageOldTimesheetItems(profile);
    const canApproveItems = canApproveTimesheetItems(profile);
    const canDisapproveItems = canDisapproveTimesheetItems(profile);
    const canBillItems = canBillTimesheetItems(profile);
    const canUnbillItems = canUnbillTimesheetItems(profile);
    const canLoadEmployees = canViewOtherTimesheetItems || canSeeEmployeesSection(profile);
    const canLoadProjects = canViewOtherTimesheetItems || canSeeProjectsSection(profile);
    const canLoadShiftTypes = canViewSchedule || canSeeShiftTypesSection(profile);
    const canLoadAbsenceTypes = canViewSchedule || canSeeAbsenceTypesSection(profile);
    const canLoadWorkCalendars = canSeeWorkCalendars(profile);
    const minEditableDate = useMemo(
        () => canEditOldTimesheetItems
            ? today.clone().add(-365, 'days').startOf('day')
            : today.clone().startOf('month'),
        [canEditOldTimesheetItems, today]
    );
    const maxEditableDate = useMemo(
        () => today.clone().startOf('day'),
        [today]
    );
    const topBarDataLoading = (canLoadEmployees && employees.length < 1)
        || (canLoadProjects && projects.length < 1)
        || (canLoadShiftTypes && shiftType.total < 1)
        || (canLoadAbsenceTypes && absenceType.total < 1)
        || (canLoadWorkCalendars && workCalendars.length < 1)
        || (canLoadWorkCalendars && years.length < 1);

    const canManageTimesheetDate = (date?: string): boolean => {
        if (!date || canEditOldTimesheetItems) {
            return true;
        }

        return !moment(date, 'YYYY-MM-DD', true).isBefore(minEditableDate, 'day');
    };

    const handleItemUpdate = (item: TimeSheetItem): Promise<void> => {
        if (!canManageTimesheetDate(item.date)) {
            return Promise.resolve();
        }

        updateItem(item);
        store.dispatch(clearSelected())
        if (item.timeshiftType === TimeSheetItemType.SHIFT) {
            return UpdateShiftFromItem(item, () => {
                return shiftType.items.find(x => x.id === item.timeshiftTypeId)!
            })
        } else if (item.timeshiftType === TimeSheetItemType.ABSENCE) {
            return UpdateAbsenceFromItem(item, () => {
                return absenceType.items.find(x => x.id === item.timeshiftTypeId)!
            })
        }

        return Promise.resolve();
    };

    const ownEmployeeId = profile.id;
    const ownEmployee = useMemo<REmployees | null>(() => {
        if (!ownEmployeeId) {
            return null;
        }

        return employees.find((employee) => employee.id === ownEmployeeId)
            ?? { id: ownEmployeeId, name: profile.name, isDefault: false };
    }, [employees, ownEmployeeId, profile.name]);

    const filterEmployees = useMemo<REmployees[]>(() => (
        canViewOtherTimesheetItems
            ? employees
            : ownEmployee ? [ownEmployee] : []
    ), [canViewOtherTimesheetItems, employees, ownEmployee]);

    const filterProjects = useMemo<PIdNameModel[]>(() => {
        if (canLoadProjects) {
            return projects;
        }

        if (ownEmployeeDetails?.projects?.length) {
            return ownEmployeeDetails.projects;
        }

        const uniqueProjects = new Map<number, PIdNameModel>();
        (data ?? []).forEach((item) => {
            if (item.timeshiftType !== TimeSheetItemType.SHIFT || !item.targetId) {
                return;
            }

            uniqueProjects.set(item.targetId, {
                id: item.targetId,
                name: item.targetName,
                isDefault: false,
            });
        });

        return Array.from(uniqueProjects.values());
    }, [canLoadProjects, data, ownEmployeeDetails?.projects, projects]);

    useEffect(() => {
        const allowedEmployeeIds = new Set(filterEmployees.map((employee) => employee.id));
        const allowedProjectIds = new Set(filterProjects.map((project) => project.id));
        const selectedEmployees = propertyFilter.selectedEmployees ?? [];
        const selectedProjects = propertyFilter.selectedProjects ?? [];
        const nextEmployees = canViewOtherTimesheetItems
            ? selectedEmployees
            : selectedEmployees.filter((employee) => allowedEmployeeIds.has(employee.originalId as number));
        const nextProjects = canLoadProjects || allowedProjectIds.size < 1
            ? selectedProjects
            : selectedProjects.filter((project) => allowedProjectIds.has(project.originalId as number));

        if (nextEmployees.length === selectedEmployees.length && nextProjects.length === selectedProjects.length) {
            return;
        }

        updateTimeTrackingPropertyFilter({
            ...propertyFilter,
            selectedEmployees: nextEmployees,
            selectedProjects: nextProjects,
        });
    }, [canLoadProjects, canViewOtherTimesheetItems, filterEmployees, filterProjects, propertyFilter]);

    const isOwnTimesheetItem = (item: TimeSheetItem): boolean => (
        Boolean(ownEmployeeId && item.employeeId === ownEmployeeId)
    );

    const canManageTimesheetItem = (item: TimeSheetItem): boolean => (
        canManageTimesheetDate(item.date)
        && (canEditTimesheetItems || (canEditOwnTimesheetItems && isOwnTimesheetItem(item)))
    );

    const setTimesheetOverride = (targetPath: string) => {
        setOverride({
            from: location.pathname,
            to: targetPath,
            label: intl.formatMessage({ id: 'timesheet' }),
        });
    };

    // Every navigation the user is expected to come back from goes through here, so a new path
    // cannot silently lose the expanded groups. Sub-pages that do not own a breadcrumb override
    // (favourites) use this directly; forms and the import page add the override below.
    const navigateAwayFromTimesheet = (targetPath: string, state?: Record<string, unknown>) => {
        armSnapshot();
        navigate(targetPath, { state: { ...state, from: location.pathname } });
    };

    const navigateToTimesheetForm = (targetPath: string, state?: Record<string, unknown>) => {
        setTimesheetOverride(targetPath);
        navigateAwayFromTimesheet(targetPath, state);
    };

    const navigateToCreateShift = (payload?: CreateShiftPayload) => {
        navigateToTimesheetForm(NAVIGATION_ROUTES.CREATE_SHIFT, {
            ...payload,
            projects: payload?.projects ?? filterProjects
        });
    };

    const resolveItemEmployeeId = (item: TimeSheetItem): number | undefined => {
        if (item.employeeId != undefined) {
            return item.employeeId;
        }

        return item.timeshiftType === TimeSheetItemType.SHIFT
            ? shifts.items.find((x) => x.id === item.itemId)?.employeeId
            : absences.items.find((x) => x.id === item.itemId)?.employeeId;
    };

    const handleAddNewItemClick = (item: TimeSheetItem) => {
        const employeeId = resolveItemEmployeeId(item);
        const payload = {
            employeeId,
            date: item.date,
            lockEmployee: employeeId !== undefined,
            projectId: item.targetId > 0 ? item.targetId : undefined,
            projectName: item.targetId > 0 ? item.targetName : undefined,
            projects: filterProjects
        };

        navigateToCreateShift(payload);
        return Promise.resolve();
    };

    const handleCopyItemClick = (item: TimeSheetItem) => {
        const employeeId = resolveItemEmployeeId(item);
        const isShift = item.timeshiftType === TimeSheetItemType.SHIFT;
        // Tags and overtime live on the full shift entity, not on the timesheet item, so resolve them from the store.
        const sourceShift = isShift ? shifts.items.find((x) => x.id === item.itemId) : undefined;
        const payload: CreateShiftPayload = {
            employeeId,
            // Leave the date empty so the copy opens on today and the user consciously picks a day
            // (avoids silently creating a duplicate on the source date).
            date: '',
            lockEmployee: employeeId !== undefined,
            projectId: isShift && item.targetId > 0 ? item.targetId : undefined,
            projectName: isShift && item.targetId > 0 ? item.targetName : undefined,
            projects: filterProjects,
            copy: {
                kind: isShift ? 'shift' : 'absence',
                typeId: item.timeshiftTypeId,
                hours: item.hours,
                note: item.note,
                internalNote: item.internalNote,
                isOvertime: sourceShift?.isOvertime ?? false,
                tags: sourceShift?.tags ?? [],
            },
        };

        navigateToCreateShift(payload);
        return Promise.resolve();
    };

    const handleEditItemClick = (item: TimeSheetItem) => {
        const target = item.timeshiftType === TimeSheetItemType.SHIFT
            ? NAVIGATION_ROUTES.EDIT_SHIFT.replace(':shiftId', String(item.itemId))
            : NAVIGATION_ROUTES.EDIT_ABSENCE.replace(':absenceId', String(item.itemId));

        navigateToTimesheetForm(target);
        return Promise.resolve();
    }

    const getEmployeeForItem = (item: TimeSheetItem): Promise<Employee> => {
        return dispatch(getEmployee(item.employeeId)).unwrap()
    }

    const beginDeleteItem = (item: TimeSheetItem) => {
        if (item.timeshiftType === TimeSheetItemType.SHIFT) {
            const shift = shifts.items.find((x) => x.id === item.itemId);
            if (!shift) {
                return;
            }

            store.dispatch(selectIdOnDelete(shift))

            return;
        }

        const absence = absences.items.find((x) => x.id === item.itemId);
        if (!absence) {
            return;
        }

        store.dispatch(selectIdOnDelete(absence))
    }

    const handleDeleteShift = (item: Shift) => {
        removeItem(shiftAsTimeSheetItem(item, employees))
        store.dispatch(clearSelected());
    }
    const handleDeleteAbsence = (item: Absence) => {
        const timeSheetItem = absenceAsTimeSheetItem(item, employees);
        if (timeSheetItem) {
            removeItem(timeSheetItem)
        }
        store.dispatch(clearSelected());
    }

    const handleItemApprove = async (item: TimeSheetItem, approve: boolean) => {
        if ((approve && !canApproveItems) || (!approve && !canDisapproveItems)) {
            return;
        }      

        updateItem({
            ...item, 
            status: 
                approve 
                    ? TimeSheetItemStatus.APPROVED 
                    : TimeSheetItemStatus.NORMAL 
        });
        store.dispatch(clearSelected());

        if (approve) {
            return ItemApprove(item);
        } else {
            return ItemDisapprove(item);
        }
    }

    const handleItemBill = async (item: TimeSheetItem, billable: boolean) => {
        if ((billable && !canBillItems) || (!billable && !canUnbillItems)) {
            return;
        }

        updateItem({
            ...item, 
            status: 
                billable 
                    ? TimeSheetItemStatus.BILLABLE 
                    : TimeSheetItemStatus.APPROVED 
        });
        store.dispatch(clearSelected());
        
        if (billable) {
            return ItemBill(item);
        } else {
            return ItemUnbill(item);
        }
    }

    const handleApproveAllClick = async (items: TimeSheetItem[]) => {
        if (!canApproveItems) {
            return;
        }

        const itemsToApprove = items
            .filter((x) => x.status === TimeSheetItemStatus.NORMAL);

        updateItems(itemsToApprove.map((x) => ({...x, status: TimeSheetItemStatus.APPROVED})));
        return ItemsBatchApprove(itemsToApprove);
    };

    const handleBillAllClick = async (items: TimeSheetItem[]): Promise<void> => {
        if (!canBillItems) {
            return;
        }

        const itemsToBill = items
            .filter((x) => x.timeshiftType === TimeSheetItemType.SHIFT
                && x.isBillable
                && x.status !== TimeSheetItemStatus.BILLABLE);

        if  (itemsToBill.length == 0)
        {
            showSnack('Nothing to bill. No available shifts or all projects are unbillable', 'info');
            return;
        }
        
        updateItems(itemsToBill.map((x) => ({...x, status: TimeSheetItemStatus.BILLABLE})));
        await ItemsBatchBill(itemsToBill);
    };

    const handleDisapproveAllClick = async (items: TimeSheetItem[]) => {
        if (!canDisapproveItems) {
            return;
        }

        updateItems(items.map((x) => ({...x, status: TimeSheetItemStatus.NORMAL})));

        return ItemsBatchDisapprove(items);
    };

    const handleCancelBillAllClick = async (items: TimeSheetItem[]) => {
        if (!canUnbillItems) {
            return;
        }

        updateItems(items.filter((x) => x.timeshiftType === TimeSheetItemType.SHIFT)
            .map((x) => ({...x, status: TimeSheetItemStatus.APPROVED})));
        
        return ItemsBatchUnbill(items);
    };

    useEffect(() => {
        if (canViewSchedule) {
            return;
        }

        if (canImportTracking) {
            navigate(NAVIGATION_ROUTES.TIMESHEET_IMPORT, { replace: true });
            return;
        }

        navigate(NAVIGATION_ROUTES.MAIN, { replace: true });
    }, [canImportTracking, canViewSchedule, navigate]);

    useEffect(() => {
        setIndicatorLoading(dataLoading);
        return () => {
            setIndicatorLoading(false);
        };
    }, [dataLoading]);


    const loadCalendarYears = useCallback((workCalendarId: number) => {
        const current = today.year();
        [current - 1, current].forEach((x) => {
            dispatch(getWorkCalendar({ workCalendarId, year: x })).unwrap().then((ry) => {
                setYears((y) => {
                    if (!y.find((yy) => yy.year === ry.year)) {
                        const state = [...y];
                        state.push(ry)
                        return state;
                    }

                    return y;
                })
            })
        });
    }, [dispatch, today]);

    useEffect(() => {
        if (!canViewSchedule) {
            return;
        }

        if (!canViewOtherTimesheetItems && profile.id && ownEmployeeDetails?.id !== profile.id) {
            dispatch(getEmployee(profile.id)).unwrap().then(setOwnEmployeeDetails).catch(console.error);
        }

        if (canLoadEmployees && employees.length < 1) {
            dispatch(getAllEmployees());
        }
        if (canLoadProjects && projects.length < 1) {
            dispatch(getProjects());
        }
        if (canLoadShiftTypes && shiftType.total < 1) {
            dispatch(getAllShiftsTypes());
        }
        if (canLoadAbsenceTypes && absenceType.total < 1) {
            dispatch(getAbsencesTypes(0));
        }
        if (canLoadProjects && projectType.total < 1) {
            dispatch(getAllProjectTypes());
        }

        if (!canLoadWorkCalendars) {
            if (profile.id && employeeWorkCalendarId < 1) {
                dispatch(getWorkCalendarIdByEmployeeId({ employeeId: profile.id }));
            }
            return;
        }

        if (workCalendars.length > 0) {
            loadCalendarYears(workCalendars[0]!.id);
            return;
        }

        dispatch(getWorkCalendars()).unwrap().then((value) => {
            loadCalendarYears(value[0]!.id);
        });
    }, [
        absenceType.total,
        canLoadAbsenceTypes,
        canLoadEmployees,
        canLoadProjects,
        canLoadShiftTypes,
        canLoadWorkCalendars,
        canViewSchedule,
        canViewOtherTimesheetItems,
        dispatch,
        employees.length,
        employeeWorkCalendarId,
        loadCalendarYears,
        ownEmployeeDetails?.id,
        profile.id,
        projects.length,
        projectType.total,
        shiftType.total,
        workCalendars.length,
    ]);

    useEffect(() => {
        if (canLoadWorkCalendars || employeeWorkCalendarId < 1 || years.length > 0) {
            return;
        }

        loadCalendarYears(employeeWorkCalendarId);
    }, [canLoadWorkCalendars, employeeWorkCalendarId, loadCalendarYears, years.length]);

    useLayoutEffect(() => {
        if (isMobile) {
            setSubheaderPortalContainer(null);
            return;
        }

        setSubheaderPortalContainer(subheaderPortalRef.current);
    }, [isMobile]);

    const importLabel = intl.formatMessage({ id: 'time_tracking_import_action', defaultMessage: 'Import' });
    const hideSchedule = isMobile && isMobileFiltersOpen;
    const gridClasses = classNames(styles.gridSection, { [styles.mobileHidden]: hideSchedule });
    const shouldLockScroll = isMobile && (isMobileFiltersOpen || isDateSelectorOpen);
    const pageBodyClasses = classNames(styles.pageBodyContent, {
        [styles.scrollLocked]: shouldLockScroll
    });
    const shouldShowMobileFooter = isMobile && !isMobileFiltersOpen;
    const pageBodyWrapperClasses = classNames(styles.pageBodyWrapper, {
        [styles.pageBodyWrapperWithFooter]: shouldShowMobileFooter
    });
    const controlsDisabled = dataLoading;
    const filteredData = useMemo(
        () => filterTimesheetItems(data ?? [], search ?? '', canViewInternalNote),
        [canViewInternalNote, data, search]
    );
    const summaryHours = usePersistedSummary({
        items: filteredData,
        controlsDisabled,
        emptySummary: EMPTY_SUMMARY_HOURS,
        storageKeys: TIMESHEET_SUMMARY_STORAGE_KEYS,
        calculateSummary: getTimesheetSummaryHours
    });
    const handleOpenImportPage = () => {
        if (!canImportTracking) {
            return;
        }
        navigateToTimesheetForm(NAVIGATION_ROUTES.TIMESHEET_IMPORT);
    };

    useEffect(() => {
        if (!isMobile || (!isMobileFiltersOpen && !isDateSelectorOpen) || typeof document === 'undefined') {
            return;
        }

        const { style } = document.body;
        const previousOverflowY = style.overflowY;
        style.overflowY = 'hidden';

        return () => {
            style.overflowY = previousOverflowY;
        };
    }, [isDateSelectorOpen, isMobile, isMobileFiltersOpen]);

    useEffect(() => {
        if (!isMobile || typeof window === 'undefined') {
            return;
        }

        if (isMobileFiltersOpen) {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
    }, [isMobile, isMobileFiltersOpen]);

    if (!canViewSchedule) {
        return null;
    }

    return <SimplePageWrapper wrapperClassName={styles.pageScope}>
        <div className={styles.pageContent}>
            <PageHeader>
                <TopBar loading={topBarDataLoading} dataLoading={dataLoading} years={years} today={today} onCreateTimeSheetItemClick={() => navigateToCreateShift()}
                    summaryHours={summaryHours}
                    onImportClick={handleOpenImportPage}
                    onFavoritesClick={() => navigateAwayFromTimesheet(NAVIGATION_ROUTES.TIMESHEET_FAVORITES)}
                    canCreateTimeSheetItem={canViewSchedule}
                    canImport={canImportTracking}
                    mobileFiltersOpen={isMobileFiltersOpen}
                    onMobileFilterVisibilityChange={setIsMobileFiltersOpen}
                    onMobileDateSelectorVisibilityChange={setIsDateSelectorOpen}
                    subheaderPortalContainer={subheaderPortalContainer}
                    filterEmployees={filterEmployees}
                    filterProjects={filterProjects}
                    filterProjectTypes={projectType.items}
                    filterShiftTypes={shiftType.items}
                    filterAbsenceTypes={absenceType.items}
                    disableEmployeesFilter={!canViewOtherTimesheetItems}
                />
            </PageHeader>
            <TimeTrackingPageBody justifyContent={'flex-start'} alignItems={'stretch'}>
                <div className={pageBodyWrapperClasses}>
                    <div className={pageBodyClasses}>
                        {!isMobile && <div ref={subheaderPortalRef} className={styles.subheaderPortal} />}
                        <TopBarMobileSections
                            years={years}
                            today={today}
                            loading={topBarDataLoading}
                            summaryHours={summaryHours}
                            mobileFiltersOpen={isMobileFiltersOpen}
                            isDateSelectorOpen={isDateSelectorOpen}
                            onMobileFilterVisibilityChange={setIsMobileFiltersOpen}
                            onMobileDateSelectorVisibilityChange={setIsDateSelectorOpen}
                            filterEmployees={filterEmployees}
                            filterProjects={filterProjects}
                            filterProjectTypes={projectType.items}
                            filterShiftTypes={shiftType.items}
                            filterAbsenceTypes={absenceType.items}
                            disableEmployeesFilter={!canViewOtherTimesheetItems}
                        />
                        <div className={gridClasses}>
                            {<TimeSheetGrid data={data}
                                ref={timesheetGridRef}
                                initialExpandedGroupKeys={restoredGroupKeys}
                                shiftTypes={shiftType}
                                absenceTypes={absenceType}
                                intl={intl}
                                projects={filterProjects}
                                onTimeshiftUpdate={handleItemUpdate}
                                onAddNewShiftClick={handleAddNewItemClick}
                                onCreateShiftClick={() => navigateToCreateShift()}
                                forceSwitch={false}
                                notifySwitch={() => undefined}
                                onEditItemClick={handleEditItemClick}
                                onCopyItemClick={handleCopyItemClick}
                                getShiftEmployee={getEmployeeForItem}
                                onDeleteItemClick={beginDeleteItem}
                                onApproveAllClick={handleApproveAllClick}
                                onBillAllClick={handleBillAllClick}
                                onDisapproveAllClick={handleDisapproveAllClick} 
                                onCancelBillAllClick={handleCancelBillAllClick}
                                onInlineApproveClick={handleItemApprove}
                                onInlineBillClick={handleItemBill}
                                disabled={controlsDisabled}
                                canManageEntry={canManageTimesheetItem}
                                canManageInternalNote={canEditTimesheetInternalNote}
                                canViewInternalNote={canViewInternalNote}
                                minEditableDate={minEditableDate}
                                maxEditableDate={maxEditableDate}
                                permissions={{
                                    approve: { canApply: canApproveItems, canCancel: canDisapproveItems },
                                    billable: { canApply: canBillItems, canCancel: canUnbillItems }
                                }}/>}
                        </div>
                    </div>
                </div>
            </TimeTrackingPageBody>
            {shouldShowMobileFooter && (canViewSchedule || canImportTracking) && (
                <div className={styles.mobileFooter}>
                    {canViewSchedule && (
                        <button
                            type='button'
                            className={classNames(styles.mobileFooterButton, styles.mobileFooterButtonSecondary, {
                                [styles.mobileFooterButtonSingle]: !canImportTracking
                            })}
                            onClick={() => navigateToCreateShift()}
                            disabled={controlsDisabled}
                        >
                            <SVGS.PlusIcon className={styles.mobileFooterIcon} />
                        </button>
                    )}
                    {canImportTracking && (
                        <button
                            type='button'
                            className={styles.mobileFooterButton}
                            onClick={handleOpenImportPage}
                            disabled={controlsDisabled}
                        >
                            <SVGS.AddChartIcon className={styles.mobileFooterIcon} />
                            <span>{importLabel}</span>
                        </button>
                    )}
                </div>
            )}
        </div>
        {/* MODALS */}
        {selectDelete && isEditShift(selectDelete) && <DeleteShiftModal shift={selectDelete as Shift} onDelete={handleDeleteShift} onCancel={() => store.dispatch(clearSelected())}/>}
        {selectDelete && isEditAbsence(selectDelete) && <DeleteAbsenceModal absence={selectDelete as Absence} onDelete={handleDeleteAbsence} onCancel={() => store.dispatch(clearSelected())}/>}
    </SimplePageWrapper>
}
